import { useReducer, useMemo, useEffect } from 'react';
import type {
  GameState,
  GameConfig,
  PlayerSetup,
  PlayerId,
  Player,
  GameActions,
  GameHelpers,
  AIDecision,
} from '../types/bountifulSea';
import {
  gameReducer,
  createInitialState,
  getMinDeclarationForPlayer,
  getMaxDeclarationForPlayer,
  canPlayerDeclare,
  mustPlayerSinkOrLie,
  canPlayerSailOn,
  getActivePlayersFromState,
} from '../utils/bountifulSea/gameLogic';
// diceUtils imported in aiLogic
import { makeAIDecision, getAIActionDelay } from '../utils/bountifulSea/aiLogic';

// ═══════════════════════════════════════════════════════════════
// BOUNTIFUL SEA - GAME STATE HOOK
// ═══════════════════════════════════════════════════════════════

export function useBountifulSeaGame(): [GameState, GameActions, GameHelpers] {
  const [state, dispatch] = useReducer(gameReducer, null, createInitialState);

  // ─────────────────────────────────────────────────────────────────
  // ACTIONS
  // ─────────────────────────────────────────────────────────────────

  const actions: GameActions = useMemo(
    () => ({
      // Setup
      startGame: (config: GameConfig, players: PlayerSetup[]) =>
        dispatch({ type: 'START_GAME', config, players }),

      resetGame: () => dispatch({ type: 'RESET_GAME' }),

      // Round flow
      beginRound: () => dispatch({ type: 'BEGIN_ROUND' }),

      confirmPlayerReady: () => dispatch({ type: 'CONFIRM_PLAYER_READY' }),

      // Voyage actions
      rollDice: () => dispatch({ type: 'ROLL_DICE' }),

      declareTotal: (amount: number) =>
        dispatch({ type: 'DECLARE_TOTAL', amount }),

      callScoundrel: (callerId: PlayerId) =>
        dispatch({ type: 'CALL_SCOUNDREL', callerId }),

      passCall: (playerId: PlayerId) =>
        dispatch({ type: 'PASS_CALL', playerId }),

      resolveCall: () => dispatch({ type: 'RESOLVE_CALL' }),

      sailOn: () => dispatch({ type: 'SAIL_ON' }),

      makePort: () => dispatch({ type: 'MAKE_PORT' }),

      sink: () => dispatch({ type: 'SINK' }),

      // Round end
      processRoundEnd: () => dispatch({ type: 'PROCESS_ROUND_END' }),

      confirmRoundSummary: () => dispatch({ type: 'CONFIRM_ROUND_SUMMARY' }),

      // AI
      triggerAIAction: () => dispatch({ type: 'AI_TAKE_ACTION' }),

      // UI
      setMessage: (message: string | null) =>
        dispatch({ type: 'SET_MESSAGE', message }),

      hideDice: () => dispatch({ type: 'HIDE_DICE' }),
    }),
    []
  );

  // ─────────────────────────────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────────────────────────────

  const helpers: GameHelpers = useMemo(() => {
    const getCurrentPlayer = (): Player | undefined => {
      return state.players[state.round.currentPlayerId];
    };

    const getPlayer = (id: PlayerId): Player | undefined => {
      return state.players[id];
    };

    const getActivePlayers = (): Player[] => {
      return getActivePlayersFromState(state);
    };

    const getNonEliminatedPlayers = (): Player[] => {
      return Object.values(state.players).filter((p) => !p.isEliminated);
    };

    const isPlayerEliminated = (playerId: PlayerId): boolean => {
      return state.players[playerId]?.isEliminated ?? false;
    };

    const getMinDeclaration = (): number => {
      const currentPlayer = getCurrentPlayer();
      return getMinDeclarationForPlayer(currentPlayer);
    };

    const getMaxPossibleRollHelper = (): number => {
      const currentPlayer = getCurrentPlayer();
      // Use getMaxDeclarationForPlayer which accounts for cumulative haul + max possible roll
      return getMaxDeclarationForPlayer(currentPlayer);
    };

    const canDeclare = (amount: number): boolean => {
      const currentPlayer = getCurrentPlayer();
      return canPlayerDeclare(currentPlayer, amount);
    };

    const canSailOn = (): boolean => {
      const currentPlayer = getCurrentPlayer();
      return canPlayerSailOn(currentPlayer, state.bountifulSea);
    };

    const mustSinkOrLie = (): boolean => {
      const currentPlayer = getCurrentPlayer();
      return mustPlayerSinkOrLie(currentPlayer);
    };

    const canCallScoundrel = (playerId: PlayerId): boolean => {
      if (state.voyageSubPhase !== 'awaitingCalls') return false;
      if (!state.callWindow.isOpen) return false;
      if (playerId === state.round.currentPlayerId) return false;
      return state.callWindow.callers.includes(playerId);
    };

    const getCallablePlayers = (): Player[] => {
      return state.callWindow.callers
        .map((id) => state.players[id])
        .filter((p): p is Player => p !== undefined && !p.isEliminated);
    };

    const calculateHaulFromDeclaration = (declaration: number): number => {
      return Math.min(declaration, state.bountifulSea);
    };

    const getPotentialHaul = (): number => {
      const currentPlayer = getCurrentPlayer();
      return currentPlayer?.currentVoyage?.haul ?? 0;
    };

    const isCurrentPlayerAI = (): boolean => {
      const currentPlayer = getCurrentPlayer();
      return currentPlayer?.isAI ?? false;
    };

    const getAIDecision = (): AIDecision | null => {
      const currentPlayer = getCurrentPlayer();
      if (!currentPlayer || !currentPlayer.isAI) return null;
      return makeAIDecision(state, currentPlayer.id);
    };

    return {
      getCurrentPlayer,
      getPlayer,
      getActivePlayers,
      getNonEliminatedPlayers,
      isPlayerEliminated,
      getMinDeclaration,
      getMaxPossibleRoll: getMaxPossibleRollHelper,
      canDeclare,
      canSailOn,
      mustSinkOrLie,
      canCallScoundrel,
      getCallablePlayers,
      calculateHaulFromDeclaration,
      getPotentialHaul,
      isCurrentPlayerAI,
      getAIDecision,
    };
  }, [state]);

  // ─────────────────────────────────────────────────────────────────
  // AI AUTO-PLAY EFFECT
  // ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    // Only run during voyage phase
    if (state.phase !== 'voyage') return;

    const currentPlayer = state.players[state.round.currentPlayerId];
    if (!currentPlayer || !currentPlayer.isAI) return;

    // Don't act during animations
    if (state.animatingDice) return;

    // Get AI decision based on current sub-phase
    let timeoutId: NodeJS.Timeout | undefined;

    const executeAIAction = () => {
      const decision = makeAIDecision(state, currentPlayer.id);
      if (!decision) return;

      switch (decision.action) {
        case 'declare':
          if (decision.value !== undefined) {
            actions.declareTotal(decision.value);
          }
          break;
        case 'sailOn':
          actions.sailOn();
          break;
        case 'makePort':
          actions.makePort();
          break;
        case 'sink':
          actions.sink();
          break;
        case 'callScoundrel':
          actions.callScoundrel(currentPlayer.id);
          break;
        case 'pass':
          actions.passCall(currentPlayer.id);
          break;
      }
    };

    // Add delay for more natural feel
    const delay = getAIActionDelay(currentPlayer.aiPersonality || 'balanced');

    // Only execute on specific sub-phases
    if (
      state.voyageSubPhase === 'declaring' ||
      state.voyageSubPhase === 'decision'
    ) {
      timeoutId = setTimeout(executeAIAction, delay);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.phase,
    state.voyageSubPhase,
    state.animatingDice,
    state.round.currentPlayerId,
    state.players,
    actions,
  ]);

  // Handle AI calling during awaiting calls phase (for non-current players)
  useEffect(() => {
    if (state.phase !== 'voyage' || state.voyageSubPhase !== 'awaitingCalls') {
      return;
    }

    if (!state.callWindow.isOpen) return;

    // Process AI callers
    const aiCallers = state.callWindow.callers.filter((id) => {
      const player = state.players[id];
      return player?.isAI && !state.callWindow.passedPlayers.includes(id);
    });

    if (aiCallers.length === 0) return;

    // Process first AI caller
    const aiCallerId = aiCallers[0];
    const aiPlayer = state.players[aiCallerId];
    if (!aiPlayer) return;

    const delay = getAIActionDelay(aiPlayer.aiPersonality || 'balanced');

    const timeoutId = setTimeout(() => {
      const decision = makeAIDecision(state, aiCallerId);
      if (decision?.action === 'callScoundrel') {
        actions.callScoundrel(aiCallerId);
      } else {
        actions.passCall(aiCallerId);
      }
    }, delay);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.phase,
    state.voyageSubPhase,
    state.callWindow.isOpen,
    state.callWindow.callers,
    state.callWindow.passedPlayers,
    state.players,
    actions,
  ]);

  // Auto-roll dice when entering rolling phase (for AI)
  useEffect(() => {
    if (state.phase !== 'voyage' || state.voyageSubPhase !== 'rolling') {
      return;
    }

    const currentPlayer = state.players[state.round.currentPlayerId];
    if (!currentPlayer) return;

    // Auto-roll for both AI and human (dice need to be rolled to proceed)
    if (!currentPlayer.currentVoyage || currentPlayer.currentVoyage.declaredTotal === null) {
      const delay = currentPlayer.isAI ? 500 : 100;
      const timeoutId = setTimeout(() => {
        actions.rollDice();
      }, delay);

      return () => clearTimeout(timeoutId);
    }
  }, [state.phase, state.voyageSubPhase, state.round.currentPlayerId, state.players, actions]);

  // Auto-confirm player ready for AI
  useEffect(() => {
    if (state.phase !== 'playerTransition') return;

    const currentPlayer = state.players[state.round.currentPlayerId];
    if (!currentPlayer?.isAI) return;

    const timeoutId = setTimeout(() => {
      actions.confirmPlayerReady();
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [state.phase, state.round.currentPlayerId, state.players, actions]);

  // Complete dice animation
  useEffect(() => {
    if (!state.animatingDice) return;

    const timeoutId = setTimeout(() => {
      dispatch({ type: 'SET_DICE_ANIMATION_COMPLETE' });
    }, 1500);

    return () => clearTimeout(timeoutId);
  }, [state.animatingDice]);

  return [state, actions, helpers];
}

export default useBountifulSeaGame;
